// src/pages/admin/StoreRequests/RequestDetail.jsx

import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { storeRequestApi } from "../../../api/storeRequestApi";
import { onboardingApi } from "../../../api/onboardingApi";
import { messageApi } from "../../../api/messageApi";
import { paymentApi } from "../../../api/paymentApi";
import { adminApi } from "../../../api/adminApi";
import { useNotifications } from "../../../context/NotificationContext";
import { toast } from "react-toastify";
import { DEFAULT_BUSINESS_PROFILE_QUESTIONS } from "../../client/StoreRequest/businessProfileQuestions";
import { DEFAULT_CONTRACT_TEMPLATES } from "../../client/StoreRequest/contractTemplates";
import { analyzeBusinessAssessment } from "./assessmentAnalysis";
import { formatChatDateTime } from "../../../utils/chatDateTime";
import "./RequestDetail.css";

const parseRequirements = (requirements) => {
  if (!requirements) return {};
  if (typeof requirements === "object") return requirements;
  if (typeof requirements !== "string") return {};

  try {
    const parsed = JSON.parse(requirements);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const formatCurrency = (amount, currency = "ZAR") =>
  `${currency} ${Number(amount || 0).toFixed(2)}`;
const EDIT_LOCKED_STATUSES = new Set([
  "approved",
  "in_progress",
  "ready_for_review",
  "live",
  "suspended",
]);

const RequestDetail = () => {
  const { id } = useParams();
  const { socket } = useNotifications();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const hasLoadedRequestRef = useRef(false);
  const refreshInFlightRef = useRef(false);

  const [request, setRequest] = useState(null);
  const [steps, setSteps] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [sendingPaymentReminder, setSendingPaymentReminder] = useState(false);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    hasLoadedRequestRef.current = false;
    setLoading(true);
    fetchRequestDetails();
  }, [id]);

  useEffect(() => {
    if (!socket) return undefined;
    const refreshRequest = (event) => {
      if (event?.storeRequestId === id) fetchRequestDetails();
    };
    socket.on("request:updated", refreshRequest);
    return () => socket.off("request:updated", refreshRequest);
  }, [id, socket]);

  const fetchRequestDetails = async () => {
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    const showLoading = !hasLoadedRequestRef.current;
    try {
      if (showLoading) setLoading(true);
      const [requestRes, stepsRes, messagesRes] = await Promise.all([
        storeRequestApi.getRequestById(id),
        onboardingApi.getStoreSteps(id),
        messageApi.getMessages(id),
      ]);

      setRequest(requestRes.data);
      setSteps(stepsRes.data || []);
      setMessages(messagesRes.data || []);
      hasLoadedRequestRef.current = true;
    } catch (error) {
      setError("Failed to load request details");
      console.error(error);
    } finally {
      if (showLoading) setLoading(false);
      refreshInFlightRef.current = false;
    }
  };

  const printDocument = (documentType) => {
    const printClass = `printing-admin-${documentType}`;
    const clearPrintClass = () => {
      document.body.classList.remove(printClass);
      window.removeEventListener("afterprint", clearPrintClass);
    };

    document.body.classList.remove(
      "printing-admin-invoice",
      "printing-admin-contract",
    );
    document.body.classList.add(printClass);
    window.addEventListener("afterprint", clearPrintClass);
    window.print();
  };

  const handleStatusUpdate = async (status) => {
    setUpdating(true);
    setError("");

    try {
      await storeRequestApi.updateStatus(id, status);
      const statusLabels = {
        approved: "Approved",
        rejected: "Rejected",
        in_progress: "In Progress",
        live: "Live",
        suspended: "Suspended",
        pending: "Pending",
      };
      toast.success(`Status updated to ${statusLabels[status] || status}`);
      fetchRequestDetails();
      setShowStatusDropdown(false);
    } catch (error) {
      setError("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleEditingAccessUpdate = async () => {
    const unlocked = !Boolean(request.ClientEditingUnlocked);
    setUpdating(true);
    setError("");
    try {
      await storeRequestApi.updateEditingAccess(id, unlocked);
      toast.success(
        unlocked
          ? "Client editing unlocked."
          : "Client editing locked.",
      );
      await fetchRequestDetails();
    } catch (error) {
      setError(
        error.response?.data?.error || "Failed to update editing access.",
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await messageApi.sendMessage(id, newMessage);
      setNewMessage("");
      await fetchRequestDetails();
      window.requestAnimationFrame(scrollToBottom);
    } catch (error) {
      setError("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!request?.LatestPaymentID) return;
    const transactionId = window.prompt(
      "Optional: enter the transaction/reference ID after verifying receipt.",
    );
    if (transactionId === null) return;

    setConfirmingPayment(true);
    setError("");
    try {
      await paymentApi.confirmPayment(
        request.LatestPaymentID,
        transactionId.trim(),
      );
      toast.success("Payment confirmed and request approved.");
      await fetchRequestDetails();
    } catch (confirmError) {
      setError(
        confirmError.response?.data?.error || "Failed to confirm payment.",
      );
    } finally {
      setConfirmingPayment(false);
    }
  };

  const handleSendPaymentReminder = async () => {
    if (
      !window.confirm(
        `Send a payment reminder to ${request.Email || "this client"}?`,
      )
    ) {
      return;
    }

    setSendingPaymentReminder(true);
    try {
      const response = await adminApi.sendPaymentReminder(id);
      toast.success(response.message || "Payment reminder sent.");
    } catch (reminderError) {
      toast.error(
        reminderError.response?.data?.error ||
          "Failed to send the payment reminder.",
      );
    } finally {
      setSendingPaymentReminder(false);
    }
  };

  const getStepStatusIcon = (status) => {
    const icons = {
      pending: "⏳",
      in_progress: "🔄",
      completed: "✅",
      failed: "❌",
      skipped: "➡️",
    };
    return icons[status] || "⏳";
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending: "status-pending",
      approved: "status-approved",
      rejected: "status-rejected",
      in_progress: "status-progress",
      live: "status-live",
      suspended: "status-suspended",
    };
    return classes[status] || "status-pending";
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "Pending",
      approved: "Approved",
      rejected: "Rejected",
      in_progress: "In Progress",
      live: "Live",
      suspended: "Suspended",
    };
    return labels[status] || status;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-ZA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getAssessmentAnswers = (requirements) => {
    if (!requirements) return null;

    let parsedRequirements = requirements;
    if (typeof requirements === "string") {
      try {
        parsedRequirements = JSON.parse(requirements);
      } catch {
        return null;
      }
    }

    const assessment = parsedRequirements?.onboardingAssessment;
    return assessment && typeof assessment === "object" ? assessment : null;
  };

  if (loading) {
    return (
      <div className="request-detail-loading">
        <div className="spinner"></div>
        <p>Loading request details...</p>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="request-detail-not-found">
        <div className="not-found-icon">🔍</div>
        <h3>Request Not Found</h3>
        <p>The store request could not be found</p>
        <button
          className="btn-primary"
          onClick={() => navigate("/admin/requests")}
        >
          Back to Requests
        </button>
      </div>
    );
  }

  const assessmentAnswers = getAssessmentAnswers(
    request.AdditionalRequirements,
  );
  const requirements = parseRequirements(request.AdditionalRequirements);
  const savedDomain = requirements.domain || {};
  const savedInvoice = requirements.invoice || {};
  const savedPackage = requirements.package || {};
  const savedBusiness = requirements.businessInfo || {};
  const savedAgreement =
    requirements.agreement?.contract ||
    requirements.contractAcceptance ||
    request.ContractAcceptance ||
    {};
  const isTrialRequest =
    Boolean(request.IsTrial) ||
    Boolean(savedDomain.isTrial) ||
    savedDomain.domainType === "subdomain";
  const requiresEditUnlock =
    Boolean(request.ApprovedDate) || EDIT_LOCKED_STATUSES.has(request.Status);
  const domainType =
    savedDomain.domainType ||
    (isTrialRequest
      ? "subdomain"
      : request.NeedsDomain
        ? "custom"
        : request.ExistingDomain
          ? "existing"
          : "");
  const savedCustomDomain =
    savedDomain.customDomain || savedDomain.preferredDomain || "";
  const selectedExtension =
    savedDomain.selectedExtension || savedDomain.extension || "";
  const fullCustomDomain =
    savedCustomDomain && selectedExtension &&
    !savedCustomDomain.endsWith(selectedExtension)
      ? `${savedCustomDomain}${selectedExtension}`
      : savedCustomDomain;
  const fullDomain = isTrialRequest
    ? savedDomain.subdomain
      ? savedDomain.subdomain.includes(".")
        ? savedDomain.subdomain
        : `${savedDomain.subdomain}.tooraflex.co.za`
      : request.Domain || request.PreferredStoreName || "N/A"
    : domainType === "existing"
      ? savedDomain.existingDomain ||
        request.ExistingDomain ||
        request.Domain ||
        request.PreferredStoreName ||
        "N/A"
      : domainType === "custom"
        ? fullCustomDomain ||
          request.ExistingDomain ||
          request.Domain ||
          request.PreferredStoreName ||
          "N/A"
        : request.Domain ||
          request.ExistingDomain ||
          request.PreferredStoreName ||
          "N/A";
  const packageTier = String(
    savedInvoice.packageTier || savedPackage.tier || request.Tier || "",
  )
    .trim()
    .toLowerCase();
  const currentPackageAmount = Number(
    savedInvoice.monthlyPackageAmount ?? savedPackage.price ?? request.Price ?? 0,
  );
  const legacyLiteInvoiceNeedsPackageCharge =
    !isTrialRequest &&
    packageTier === "lite" &&
    Number(savedInvoice.packageAmount) === 0 &&
    currentPackageAmount > 0;
  const invoicePackageAmount = Number(
    legacyLiteInvoiceNeedsPackageCharge
      ? currentPackageAmount
      : (savedInvoice.packageAmount ??
          (isTrialRequest ? 0 : currentPackageAmount)),
  );
  const legacyOnboardingCharge = Number(savedInvoice.onboardingFee ?? 0);
  const packageMonthlyFee = Number(
    savedInvoice.monthlyPackageAmount ??
      savedPackage.price ??
      request.Price ??
      invoicePackageAmount,
  );
  const invoiceSetupFee = Number(
    savedInvoice.packageSetupFee ?? savedPackage.setupFee ?? 0,
  );
  const invoiceDomainTotal = Number(
    savedInvoice.domainTotal ??
      savedDomain.domainPrice ??
      savedDomain.domainPriceWithTax ??
      0,
  );
  const invoiceDomainListPrice = Number(
    savedInvoice.domainListPrice ?? invoiceDomainTotal,
  );
  const invoiceDomainAllowance = Number(savedInvoice.domainAllowance ?? 0);
  const invoiceTotalDue = Number(
    legacyLiteInvoiceNeedsPackageCharge
      ? invoicePackageAmount + invoiceSetupFee + invoiceDomainTotal
      : savedInvoice.totalDue ??
          (request.LatestPaymentAmount
            ? request.LatestPaymentAmount
            : (savedInvoice.onboardingFee !== undefined
                ? legacyOnboardingCharge
                : invoicePackageAmount) +
              invoiceSetupFee +
              invoiceDomainTotal),
  );
  const invoiceCurrency = savedInvoice.currency || "ZAR";
  const reconstructedBillingSummary = isTrialRequest
    ? "Trial onboarding: no amount is due now."
    : [
        `Selected package: ${savedInvoice.packageName || savedPackage.name || request.PackageName || "Store package"} (${savedInvoice.packageTier || savedPackage.tier || request.Tier || "standard"}).`,
        `Package charge due now: ${formatCurrency(invoicePackageAmount, invoiceCurrency)} per ${savedInvoice.packageBillingPeriod || savedPackage.billingPeriod || "month"}.`,
        `Annual selected domain list price: ${formatCurrency(invoiceDomainListPrice, invoiceCurrency)}.`,
        `Included annual domain allowance: ${formatCurrency(invoiceDomainAllowance, invoiceCurrency)}.`,
        `Annual domain charge after allowance: ${formatCurrency(invoiceDomainTotal, invoiceCurrency)}.`,
        `Total due now: ${formatCurrency(invoiceTotalDue, invoiceCurrency)}.`,
      ].join("\n");
  const isReconstructedInvoice =
    !requirements.invoice || typeof requirements.invoice !== "object";
  const contractTemplate =
    DEFAULT_CONTRACT_TEMPLATES.find(
      (template) =>
        template.id === (savedAgreement.contractId || "online-store"),
    ) || DEFAULT_CONTRACT_TEMPLATES[0];
  const contractReplacements = {
    "{{AGREEMENT_DATE}}": savedAgreement.acceptedAt
      ? new Date(savedAgreement.acceptedAt).toLocaleDateString("en-ZA", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        })
      : formatDate(request.SubmittedDate),
    "{{AGREEMENT_NUMBER}}":
      savedAgreement.agreementNumber || request.RequestNumber || "N/A",
    "{{CLIENT_FULL_NAME}}":
      savedBusiness.legalFullName ||
      savedInvoice.clientFullName ||
      savedAgreement.clientFullName ||
      savedBusiness.contactName ||
      request.FullName ||
      "N/A",
    "{{BUSINESS_NAME}}":
      savedBusiness.businessName || request.BusinessName || "Client",
    "{{CONTACT_NAME}}": savedBusiness.contactName || request.FullName || "N/A",
    "{{PHONE}}": savedBusiness.phone || request.MobileNumber || "N/A",
    "{{EMAIL}}": savedBusiness.email || request.Email || "N/A",
    "{{PACKAGE_NAME}}": `${savedInvoice.packageName || savedPackage.name || request.PackageName || "N/A"}${savedInvoice.packageTier || savedPackage.tier || request.Tier ? ` (${savedInvoice.packageTier || savedPackage.tier || request.Tier})` : ""}`,
    "{{PRODUCT_LIMIT}}": savedPackage.productLimit || "200",
    "{{MONTHLY_FEE}}": `${formatCurrency(packageMonthlyFee, invoiceCurrency)} per ${savedInvoice.packageBillingPeriod || savedPackage.billingPeriod || "month"} (charged now)`,
    "{{SETUP_FEE}}": formatCurrency(invoiceSetupFee, invoiceCurrency),
    "{{DOMAIN_FEE}}": `${domainType === "existing" ? "Existing domain (no registration fee)" : "Domain total"}: ${fullDomain} — ${formatCurrency(invoiceDomainTotal, invoiceCurrency)}`,
    "{{UPFRONT_DUE}}": formatCurrency(invoiceTotalDue, invoiceCurrency),
    "{{DOMAIN_TERMS}}":
      domainType === "existing"
        ? `The Client has provided the existing domain ${fullDomain}. No domain registration fee is charged.`
        : isTrialRequest
          ? `A Toora Flex trial subdomain is generated from the business name: ${fullDomain}.`
          : `The preferred new domain ${fullDomain} will be registered as part of the store request for the total shown above.`,
    "{{ONBOARDING_BILLING_SUMMARY}}": reconstructedBillingSummary,
    "{{TRIAL_TERMS}}": isTrialRequest
      ? "The selected trial has no upfront charge and uses a Toora Flex subdomain only. A custom domain and paid plan may be selected after the trial."
      : "Not applicable. The selected plan is a paid subscription.",
  };
  const reconstructedContract = Object.entries(contractReplacements).reduce(
    (content, [token, value]) => content.split(token).join(String(value)),
    contractTemplate.content,
  );
  const savedContractDocument = requirements.contractDocument;
  const contractContent =
    typeof savedContractDocument?.content === "string"
      ? savedContractDocument.content
      : reconstructedContract;
  const contractTitle =
    savedContractDocument?.title || contractTemplate.title;
  const isReconstructedContract =
    typeof savedContractDocument?.content !== "string";
  const assessmentQuestions = DEFAULT_BUSINESS_PROFILE_QUESTIONS.flatMap(
    (section) => section.questions,
  );
  const assessmentAnalysis = analyzeBusinessAssessment(
    assessmentAnswers,
    assessmentQuestions,
  );
  const assessmentSnapshots = [
    ["Business stage", assessmentAnalysis?.categories.businessStage],
    ["Product readiness", assessmentAnalysis?.categories.productsReady],
    ["Marketing plan", assessmentAnalysis?.categories.marketingPlan],
    ["Target launch", assessmentAnalysis?.categories.targetLaunchDate],
    [
      "Subscription understanding",
      assessmentAnalysis?.categories.subscriptionUnderstood,
    ],
  ].filter(([, item]) => item?.answer != null && item.answer !== "");

  return (
    <div className="request-detail-page">
      {/* Header */}
      <div className="request-detail-header">
        <div className="header-left">
          <button
            className="back-btn"
            onClick={() => navigate("/admin/requests")}
          >
            <i className="fas fa-arrow-left"></i> Back
          </button>
          <div className="header-info">
            <h1>{request.BusinessName}</h1>
            <div className="header-meta">
              <span className="request-number">
                #{request.RequestNumber || "N/A"}
              </span>
              <span className="header-divider">•</span>
              <span className="package-name">{request.PackageName}</span>
              <span className="header-divider">•</span>
              <span className="submitted-date">
                Submitted {formatDate(request.SubmittedDate)}
              </span>
            </div>
          </div>
        </div>
        <div className="header-right">
          <a className="communication-jump-link" href="#request-communication">
            <i className="fas fa-comments" /> Communication
          </a>
          <span
            className={`status-badge ${getStatusBadgeClass(request.Status)}`}
          >
            {getStatusLabel(request.Status)}
          </span>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" onClick={() => setError("")}>
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </div>
      )}
      {/* Main Content */}
      <div className="request-detail-grid">
        {/* Left Column */}
        <div className="detail-left-column">
          {/* Status Actions */}
          <div className="detail-card status-actions-card">
            <h3>Status Actions</h3>
            <div className="status-actions">
              <div className="status-action-group">
                <button
                  className="status-btn status-btn-approve"
                  onClick={() => handleStatusUpdate("approved")}
                  disabled={updating || request.Status === "approved"}
                >
                  <i className="fas fa-check-circle"></i> Approve
                </button>
                <button
                  className="status-btn status-btn-reject"
                  onClick={() => handleStatusUpdate("rejected")}
                  disabled={updating || request.Status === "rejected"}
                >
                  <i className="fas fa-times-circle"></i> Reject
                </button>
              </div>
              <div className="status-action-group">
                <button
                  className="status-btn status-btn-progress"
                  onClick={() => handleStatusUpdate("in_progress")}
                  disabled={updating || request.Status === "in_progress"}
                >
                  <i className="fas fa-play-circle"></i> Start Onboarding
                </button>
                <button
                  className="status-btn status-btn-live"
                  onClick={() => handleStatusUpdate("live")}
                  disabled={updating || request.Status === "live"}
                >
                  <i className="fas fa-rocket"></i> Launch Store
                </button>
              </div>
              <button
                className="status-btn status-btn-suspend"
                onClick={() => handleStatusUpdate("suspended")}
                disabled={updating || request.Status === "suspended"}
              >
                <i className="fas fa-pause-circle"></i> Suspend
              </button>
            </div>
            <div className="client-editing-access">
              <p>
                Client editing is{" "}
                <strong>
                  {request.ClientEditingUnlocked
                    ? "unlocked by an admin"
                    : requiresEditUnlock
                      ? "locked after approval"
                      : "available until approval"}
                </strong>
                .
              </p>
              {(requiresEditUnlock || request.ClientEditingUnlocked) && (
                <button
                  className={`status-btn ${
                    request.ClientEditingUnlocked
                      ? "status-btn-reject"
                      : "status-btn-approve"
                  }`}
                  onClick={handleEditingAccessUpdate}
                  disabled={updating}
                >
                  <i
                    className={`fas ${
                      request.ClientEditingUnlocked
                        ? "fa-lock"
                        : "fa-lock-open"
                    }`}
                    aria-hidden="true"
                  />
                  {request.ClientEditingUnlocked
                    ? requiresEditUnlock
                      ? "Lock client editing"
                      : "Clear editing override"
                    : "Unlock client editing"}
                </button>
              )}
            </div>
          </div>

          <div className="detail-card">
            <h3>
              <i className="fas fa-credit-card"></i> Payment
            </h3>
            {request.LatestPaymentID ? (
              <>
                <p>
                  <strong>Status:</strong> {request.PaymentStatus}
                </p>
                <p>
                  <strong>Amount:</strong>{" "}
                  {request.LatestPaymentCurrency || "ZAR"}{" "}
                  {Number(request.LatestPaymentAmount || 0).toFixed(2)}
                </p>
                <p>
                  <strong>Method:</strong> {request.LatestPaymentMethod}
                </p>
                <p>
                  <strong>Reference:</strong>{" "}
                  {request.LatestPaymentReference || "N/A"}
                </p>
                {request.PaymentStatus === "pending" && (
                  <button
                    className="status-btn status-btn-approve"
                    onClick={handleConfirmPayment}
                    disabled={confirmingPayment}
                  >
                    <i className="fas fa-check-circle"></i>{" "}
                    {confirmingPayment ? "Confirming..." : "Confirm payment"}
                  </button>
                )}
              </>
            ) : request.IsTrial ? (
              <p>
                Trial request — no payment is due until{" "}
                {request.TrialEndsAt
                  ? formatDate(request.TrialEndsAt)
                  : "the trial ends"}
                .
              </p>
            ) : (
              <p>No payment has been recorded for this request.</p>
            )}
            {!request.IsTrial &&
              String(request.PaymentStatus || "").toLowerCase() !==
                "completed" &&
              !["rejected", "suspended", "live"].includes(
                String(request.Status || "").toLowerCase(),
              ) && (
                <button
                  className="status-btn payment-reminder-btn"
                  type="button"
                  onClick={handleSendPaymentReminder}
                  disabled={sendingPaymentReminder}
                >
                  <i className="fas fa-envelope" aria-hidden="true"></i>
                  {sendingPaymentReminder
                    ? "Sending reminder..."
                    : "Send payment reminder"}
                </button>
              )}
          </div>

          {/* Client Information */}
          <div className="detail-card client-info-card">
            <h3>
              <i className="fas fa-user"></i> Client Information
            </h3>
            <div className="client-info-grid">
              <div className="info-item">
                <span className="info-label">Full Name</span>
                <span className="info-value">
                  {savedBusiness.legalFullName ||
                    savedAgreement.clientFullName ||
                    request.FullName ||
                    "N/A"}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Email</span>
                <span className="info-value">{request.Email || "N/A"}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Phone</span>
                <span className="info-value">
                  {request.MobileNumber || "N/A"}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Business</span>
                <span className="info-value">
                  {request.BusinessName || "N/A"}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Category</span>
                <span className="info-value">
                  {request.BusinessCategory || "N/A"}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Package</span>
                <span className="info-value">
                  {request.PackageName || "N/A"}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Domain</span>
                <span className="info-value">{fullDomain}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Submitted</span>
                <span className="info-value">
                  {formatDate(request.SubmittedDate)}
                </span>
              </div>
            </div>
          </div>

          <div className="detail-card document-card">
            <div className="document-card-header">
              <h3>
                <i className="fas fa-file-invoice-dollar"></i> Invoice
              </h3>
              <button
                className="document-download-btn"
                type="button"
                title="Opens the print dialog; choose Save as PDF to download."
                onClick={() => printDocument("invoice")}
              >
                <i className="fas fa-download"></i> Download PDF
              </button>
            </div>
            <p className="document-note">
              {isReconstructedInvoice
                ? "This invoice is reconstructed from the saved request and payment details; the original invoice file was not archived."
                : "This invoice is generated from the invoice details saved with the request."}
            </p>
            <div className="print-document invoice-document">
              <div className="document-title-row">
                <div>
                  <h2>Invoice</h2>
                  <p>Toora Flex (Pty) Ltd</p>
                </div>
                <div className="invoice-number">
                  <span>Invoice number</span>
                  <strong>
                    {savedInvoice.invoiceNumber || request.RequestNumber || "N/A"}
                  </strong>
                </div>
              </div>
              <div className="invoice-meta">
                <p>
                  <strong>Bill to:</strong>{" "}
                  {savedBusiness.businessName || request.BusinessName || "N/A"}
                </p>
                <p>
                  <strong>Client:</strong>{" "}
                  {savedBusiness.legalFullName ||
                    savedInvoice.clientFullName ||
                    savedAgreement.clientFullName ||
                    request.FullName ||
                    "N/A"}
                </p>
                <p>
                  <strong>Email:</strong> {savedBusiness.email || request.Email || "N/A"}
                </p>
                <p>
                  <strong>Date:</strong> {formatDate(request.SubmittedDate)}
                </p>
              </div>
              <table className="invoice-lines">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {savedInvoice.packageName ||
                        savedPackage.name ||
                        request.PackageName ||
                        "Store package"}
                    </td>
                    <td>
                      {formatCurrency(invoicePackageAmount, invoiceCurrency)}
                    </td>
                  </tr>
                  {legacyOnboardingCharge > 0 && (
                    <tr>
                      <td>Previous invoice charge</td>
                      <td>
                        {formatCurrency(legacyOnboardingCharge, invoiceCurrency)}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td>Setup fee</td>
                    <td>{formatCurrency(invoiceSetupFee, invoiceCurrency)}</td>
                  </tr>
                  <tr>
                    <td>
                      Annual domain list price
                      {fullDomain !== "N/A" ? ` (${fullDomain})` : ""}
                    </td>
                    <td>{formatCurrency(invoiceDomainListPrice, invoiceCurrency)}</td>
                  </tr>
                  {invoiceDomainAllowance > 0 && (
                    <tr>
                      <td>Included domain allowance</td>
                      <td>
                        -{formatCurrency(invoiceDomainAllowance, invoiceCurrency)}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td>
                      Domain charge after allowance
                    </td>
                    <td>{formatCurrency(invoiceDomainTotal, invoiceCurrency)}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <th>Total</th>
                    <th>{formatCurrency(invoiceTotalDue, invoiceCurrency)}</th>
                  </tr>
                </tfoot>
              </table>
              {savedInvoice.paymentMethod && (
                <p className="invoice-payment-method">
                  Payment method: {savedInvoice.paymentMethod}
                </p>
              )}
            </div>
          </div>

          <div className="detail-card document-card">
            <div className="document-card-header">
              <h3>
                <i className="fas fa-file-contract"></i> Contract
              </h3>
              <button
                className="document-download-btn"
                type="button"
                title="Opens the print dialog; choose Save as PDF to download."
                onClick={() => printDocument("contract")}
              >
                <i className="fas fa-download"></i> Download PDF
              </button>
            </div>
            {isReconstructedContract && (
              <p className="document-note">
                This contract is reconstructed from the saved request details;
                the original document text was not archived for this request.
              </p>
            )}
            <div className="print-document contract-document">
              <h2>{contractTitle}</h2>
              <pre>{contractContent}</pre>
            </div>
          </div>

          {assessmentAnswers && (
            <div className="detail-card assessment-card">
              <h3>
                <i className="fas fa-lightbulb"></i> Business Readiness
              </h3>
              <p className="assessment-intro">
                A practical planning aid based on the client’s answers. It is
                guidance, not a prediction of sales or business success.
              </p>

              {assessmentAnalysis && (
                <section
                  className="assessment-analysis"
                  aria-label="Assessment analysis"
                >
                  <div className="assessment-summary-grid">
                    <div className="assessment-summary-item assessment-score">
                      <span className="assessment-summary-label">
                        Readiness index
                      </span>
                      <strong>
                        {assessmentAnalysis.score === null
                          ? "—"
                          : `${assessmentAnalysis.score}/100`}
                      </strong>
                      <span>{assessmentAnalysis.level}</span>
                    </div>
                    <div className="assessment-summary-item">
                      <span className="assessment-summary-label">
                        Support recommendation
                      </span>
                      <strong>
                        {assessmentAnalysis.supportLevel || "Not assessed"}
                      </strong>
                      <span>
                        {assessmentAnalysis.supportFactors.length
                          ? assessmentAnalysis.supportFactors.join(" · ")
                          : "No clear support flags"}
                      </span>
                    </div>
                    <div className="assessment-summary-item">
                      <span className="assessment-summary-label">
                        Scoring coverage
                      </span>
                      <strong>{assessmentAnalysis.coverage}%</strong>
                      <span>of weighted assessment areas answered</span>
                    </div>
                  </div>

                  <p className="assessment-algorithm-note">
                    Score uses business stage (20%), product readiness (30%),
                    marketing plan (20%), launch timing (15%) and subscription
                    understanding (15%). Missing or unrecognized answers are
                    excluded, and the score is recalculated from available
                    answers.
                  </p>

                  {assessmentSnapshots.length > 0 && (
                    <div className="assessment-snapshots">
                      {assessmentSnapshots.map(([label, item]) => (
                        <div className="assessment-snapshot" key={label}>
                          <span>{label}</span>
                          <strong>
                            {Array.isArray(item.answer)
                              ? item.answer.join(", ")
                              : item.answer}
                          </strong>
                        </div>
                      ))}
                    </div>
                  )}

                  {assessmentAnalysis.recommendations.length > 0 && (
                    <div className="assessment-recommendations">
                      <h4>Suggested next actions</h4>
                      <ul>
                        {assessmentAnalysis.recommendations.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </section>
              )}

              <dl className="assessment-answer-list">
                {Object.entries(assessmentAnswers)
                  .filter(([, answer]) =>
                    Array.isArray(answer)
                      ? answer.length > 0
                      : String(answer || "").trim().length > 0,
                  )
                  .map(([question, answer]) => (
                    <div className="assessment-answer" key={question}>
                      <dt>
                        {assessmentQuestions.find(
                          (item) => item.id === question,
                        )?.label ||
                          question
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (letter) => letter.toUpperCase())}
                      </dt>
                      <dd>
                        {Array.isArray(answer) ? answer.join(", ") : answer}
                      </dd>
                    </div>
                  ))}
              </dl>
            </div>
          )}

          {/* Onboarding Progress */}
          <div className="detail-card progress-card">
            <h3>
              <i className="fas fa-tasks"></i> Onboarding Progress
            </h3>
            <div className="progress-container">
              <div className="progress-bar-wrapper">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${request.TotalSteps > 0 ? Math.round((request.CompletedSteps / request.TotalSteps) * 100) : 0}%`,
                    }}
                  ></div>
                </div>
                <span className="progress-text">
                  {request.CompletedSteps || 0}/{request.TotalSteps || 0} steps
                  completed
                </span>
              </div>
            </div>
            <div className="steps-grid">
              {steps.map((step) => (
                <div
                  key={step.OnboardingStepID}
                  className={`step-item ${step.ProgressStatus === "completed" ? "completed" : ""} ${step.ProgressStatus === "in_progress" ? "in-progress" : ""}`}
                >
                  <span className="step-icon">
                    {getStepStatusIcon(step.ProgressStatus || "pending")}
                  </span>
                  <span className="step-name">
                    Step {step.StepNumber}: {step.Name}
                  </span>
                  <span className="step-status-label">
                    {step.ProgressStatus || "Pending"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Messages */}
        <div className="detail-right-column">
          <div className="detail-card messages-card" id="request-communication">
            <div className="messages-header">
              <div className="request-chat-heading">
                <span className="request-chat-avatar">
                  <i className="fas fa-comments" />
                </span>
                <div>
                  <h3>Client communication</h3>
                  <span>
                    Store request #{request.RequestNumber || "Request"} ·{" "}
                    {messages.length} message{messages.length === 1 ? "" : "s"}
                  </span>
                </div>
              </div>
              <span className="request-chat-channel">
                <i className="fas fa-lock" /> Private
              </span>
            </div>
            <div className="messages-container">
              {messages.length === 0 ? (
                <div className="no-messages">
                  <span className="request-chat-empty-icon">
                    <i className="fas fa-comment-dots" />
                  </span>
                  <p>Start the conversation</p>
                  <span>Your messages with this client will appear here.</span>
                </div>
              ) : (
                <div className="messages-list">
                  {messages.map((msg) => (
                    <div
                      key={msg.MessageID}
                      className={`message-item ${msg.SenderType === "admin" ? "message-admin" : "message-client"}`}
                    >
                      <div className="message-avatar">
                        <i
                          className={`fas ${msg.SenderType === "admin" ? "fa-headset" : "fa-user"}`}
                        />
                      </div>
                      <div className="message-content">
                        <div className="message-header">
                          <span className="message-sender">
                            {msg.SenderType === "admin"
                              ? "Toora Flex team"
                              : request.FullName || request.BusinessName}
                          </span>
                          <span className="message-time">
                            {formatChatDateTime(msg.CreatedAt)}
                          </span>
                        </div>
                        <div className="message-body">{msg.Message}</div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>
            <form className="message-form" onSubmit={handleSendMessage}>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Write a message to the client..."
                className="message-input"
                disabled={sending}
                maxLength={5000}
                aria-label="Write a communication message"
              />
              <button
                type="submit"
                className="btn-send-message"
                disabled={!newMessage.trim() || sending}
              >
                {sending ? "..." : <i className="fas fa-paper-plane"></i>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestDetail;
